/**
 * Spiral Collection Slider
 *
 * <spiral-collection-slider> turns its card list into a 3D helix. One shared
 * rotation value drives every card: a card's position along the helix is
 *
 *   p     = wrap(index - rotation / stepAngle, totalCards)   (in card units)
 *   angle = p * stepAngle                                    (around the axis)
 *   y     = p * stepY                                        (along the axis)
 *
 * so increasing the rotation turns the helix *and* screws it upward, and the
 * modulo wrap recycles cards from one end to the other seamlessly.
 *
 * Each frame only writes transforms and opacity:
 * - card transform  (position on the helix)
 * - card opacity    (depth + top/bottom fade)
 * - sharp image opacity (crossfades to a static, pre-blurred copy behind it)
 * - shade / info opacity
 *
 * Each element instance owns its state, listeners and rAF loop, so several
 * sections can live on one page, and the Theme Editor's section reloads are
 * handled by connectedCallback / disconnectedCallback.
 */
(() => {
  if (customElements.get('spiral-collection-slider')) return;

  const TAU = Math.PI * 2;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const smoothstep = (a, b, v) => {
    const t = clamp((v - a) / (b - a), 0, 1);
    return t * t * (3 - 2 * t);
  };
  const wrap = (v, size) => ((((v + size / 2) % size) + size) % size) - size / 2;

  class SpiralCollectionSlider extends HTMLElement {
    connectedCallback() {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      if (this.reduceMotion.matches) return; // static scroll-snap carousel from CSS

      this.helix = this.querySelector('.spiral__helix');
      this.originals = Array.from(this.querySelectorAll('.spiral__card'));
      if (!this.helix || this.originals.length === 0) return;

      const d = this.dataset;
      this.settings = {
        perTurn: parseInt(d.perTurn, 10) || 9,
        radius: parseFloat(d.radius) || 420,
        gap: parseFloat(d.gap) || 40,
        speed: parseFloat(d.speed) || 12, // degrees per second (continuous mode)
        // Autoplay: 'continuous' spin, 'step' one card every interval, or 'off'
        mode: ['continuous', 'step', 'off'].includes(d.autoplay) ? d.autoplay : 'continuous',
        interval: Math.max(1, parseFloat(d.interval) || 3) * 1000,
        direction: d.direction === 'reverse' ? -1 : 1,
        pauseOnHover: d.pauseOnHover !== 'false',
        // Phones can use their own autoplay mode ('same' = follow desktop)
        mobileMode: ['continuous', 'step', 'off'].includes(d.mobileAutoplay) ? d.mobileAutoplay : 'same',
        scrollDriven: d.scrollDriven === 'true',
        backOpacity: clamp((parseFloat(d.backOpacity) || 45) / 100, 0, 1),
      };

      this.rotation = 0;
      this.velocity = 0; // deg per ms from drag inertia
      this.stepClock = 0; // ms since the last autoplay step
      this.motion = 0; // 0 = resting, 1 = moving fast (drives lift + shadows)
      this.prevRotation = 0;
      this.target = null; // keyboard / focus snapping target
      this.hovered = false;
      this.focused = false;
      this.editorPaused = false;
      this.visible = true;
      this.frame = null;
      this.last = 0;

      this.classList.add('is-3d');
      this.measure();
      this.buildLoop();
      this.cacheCards();
      this.bind();
      this.render(); // first frame immediately, so nothing jumps
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.cleanup?.forEach((fn) => fn());
      this.cleanup = [];
    }

    /* ---------- Geometry ---------- */

    measure() {
      const mobile = this.mobileQuery.matches;
      const s = this.settings;
      const card = this.originals[0];
      // One layout read per (re)measure, never inside the animation loop.
      this.cardW = card.offsetWidth;
      this.cardH = card.offsetHeight;
      this.viewH = this.offsetHeight;
      this.mobile = mobile;
      this.mode = mobile && s.mobileMode !== 'same' ? s.mobileMode : s.mode;
      this.perTurn = mobile ? Math.max(5, s.perTurn - 3) : s.perTurn;
      // On phones keep side cards mostly on screen.
      const preferred = mobile ? Math.min(s.radius * 0.55, window.innerWidth * 0.4) : s.radius;
      // Neighbouring cards must not intersect: chord between them >= card width.
      const depth = parseFloat(getComputedStyle(this).getPropertyValue('--spiral-depth')) || 0;
      const minRadius = (this.cardW / (2 * Math.sin(Math.PI / this.perTurn))) * 1.04 + depth;
      this.depthHalf = depth / 2;
      this.radius = Math.max(preferred, minRadius);
      // Gap between turns keeps a clear "staircase" between front cards.
      this.pitch = this.cardH + (mobile ? s.gap * 0.6 : s.gap);
      this.stepAngle = 360 / this.perTurn;
      this.stepY = this.pitch / this.perTurn;
    }

    /** Clone cards so the helix is long enough to loop out of view. */
    buildLoop() {
      this.helix.querySelectorAll('.spiral__card[data-clone]').forEach((el) => el.remove());
      const needed = Math.ceil((this.viewH + this.cardH * 2) / this.stepY) + this.perTurn;
      this.total = clamp(Math.max(needed, this.originals.length, this.perTurn * 2), 1, 90);
      const fragment = document.createDocumentFragment();
      for (let i = this.originals.length; i < this.total; i += 1) {
        const clone = this.originals[i % this.originals.length].cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));
        fragment.appendChild(clone);
      }
      this.helix.appendChild(fragment);
    }

    cacheCards() {
      this.cards = Array.from(this.helix.querySelectorAll('.spiral__card')).map((el, index) => ({
        el,
        index,
        sharp: el.querySelector('.spiral__img--sharp'),
        blur: el.querySelector('.spiral__img--blur'),
        shade: el.querySelector('.spiral__shade'),
        info: el.querySelector('.spiral__info'),
        gloss: el.querySelector('.spiral__gloss'),
        cast: el.querySelector('.spiral__cast'),
        floor: el.querySelector('.spiral__floor'),
        back: null,
      }));
    }

    /* ---------- Rendering (single shared rotation) ---------- */

    render() {
      const { cards, total, stepAngle, stepY, radius, cardH, viewH } = this;
      const backOpacity = this.settings.backOpacity;
      const fadeStart = viewH / 2 - cardH * 0.55;
      const fadeLength = cardH * 0.9;

      for (let i = 0; i < cards.length; i += 1) {
        const c = cards[i];
        const p = wrap(c.index - this.rotation / stepAngle, total);
        const angle = p * stepAngle;
        const y = p * stepY;
        const facing = (Math.cos((angle * TAU) / 360) + 1) / 2; // 1 = facing camera, 0 = facing away
        const edge = clamp(1 - (Math.abs(y) - fadeStart) / fadeLength, 0, 1);
        const scale = 0.9 + 0.12 * facing;
        const sin = Math.sin((angle * TAU) / 360);
        // Front cards lift toward the viewer while the helix moves.
        const lift = this.motion * facing * facing * 28;

        c.el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0) rotateY(${angle.toFixed(3)}deg) translateZ(${(radius + lift).toFixed(2)}px) scale(${scale.toFixed(4)})`;

        // Cast shadow behind the slab: light comes from the front, slightly
        // above, so the shadow falls away from the turn and drops lower and
        // wider (softer) the more the card is lifted.
        if (c.cast) {
          const sx = (-sin * 34 - this.motion * sin * 26).toFixed(1);
          const sy = (22 + this.motion * 34).toFixed(1);
          const ss = (0.96 + this.motion * 0.08).toFixed(3);
          c.cast.style.transform = `translate3d(${sx}px, ${sy}px, ${(-this.depthHalf - 18 - lift * 0.6).toFixed(1)}px) scale(${ss})`;
          c.cast.style.opacity = (smoothstep(0.1, 0.9, facing) * (0.75 + this.motion * 0.25) * edge).toFixed(3);
        }
        // Product floor shadow slides and stretches with the angle.
        if (c.floor) {
          c.floor.style.transform = `translate3d(${(-sin * 22).toFixed(1)}%, 0, 0) scale(${(1 - Math.abs(sin) * 0.25 + this.motion * 0.12).toFixed(3)}, 1)`;
          c.floor.style.opacity = (0.55 + facing * 0.45 - this.motion * 0.25).toFixed(3);
        }
        // No opacity on the card itself: opacity would flatten its 3D faces.
        // Depth is shown by darkening the front face instead.
        const sharpness = smoothstep(0.45, 0.8, facing);
        if (c.sharp) c.sharp.style.opacity = sharpness.toFixed(3);
        // Blend mode is multiply, so the blurred copy must fade out under the
        // sharp image or it shows through as a soft halo.
        if (c.blur) c.blur.style.opacity = (1 - sharpness).toFixed(3);
        if (c.shade) c.shade.style.opacity = ((1 - smoothstep(0.15, 1, facing)) * (1 - backOpacity) + (1 - edge) * 0.6).toFixed(3);
        // Light reflection slides across the face as the card turns.
        if (c.gloss) c.gloss.style.transform = `translate3d(${(Math.sin((angle * TAU) / 360) * -70).toFixed(1)}%, 0, 0)`;
        if (c.info) c.info.style.opacity = smoothstep(0.78, 0.95, facing).toFixed(3);

        // Toggle clickability only when it changes (no per-frame class churn).
        const isBack = facing < 0.6 || edge < 0.5;
        if (isBack !== c.back) {
          c.back = isBack;
          c.el.classList.toggle('is-back', isBack);
        }
      }
    }

    /* ---------- Animation loop ---------- */

    start() {
      if (this.frame || !this.visible || document.hidden) return;
      this.last = performance.now();
      this.frame = requestAnimationFrame((t) => this.tick(t));
    }

    stop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    tick(now) {
      const dt = Math.min(64, now - this.last);
      this.last = now;

      if (this.dragging) {
        // rotation is updated directly by pointer events
      } else if (this.target !== null) {
        const delta = this.target - this.rotation;
        this.rotation += delta * Math.min(1, dt * 0.012);
        if (Math.abs(delta) < 0.05) {
          this.rotation = this.target;
          this.target = null;
        }
      } else {
        if (Math.abs(this.velocity) > 0.0005) {
          this.rotation += this.velocity * dt;
          this.velocity *= Math.pow(0.94, dt / 16);
        } else {
          this.velocity = 0;
        }
        const s = this.settings;
        const paused = (s.pauseOnHover && this.hovered) || this.focused || this.editorPaused;
        if (this.mode === 'continuous' && !paused) {
          // Ease auto-rotation back in as inertia dies down.
          const blend = 1 - clamp(Math.abs(this.velocity) / 0.05, 0, 1);
          this.rotation += (s.speed / 1000) * dt * blend * s.direction;
        } else if (this.mode === 'step' && !paused && this.velocity === 0) {
          // Advance one card every interval, easing into place.
          this.stepClock += dt;
          if (this.stepClock >= s.interval) {
            this.stepClock = 0;
            this.step(s.direction);
          }
        }
      }

      if (this.settings.scrollDriven && this.scrollDelta) {
        this.rotation += this.scrollDelta * 0.25;
        this.scrollDelta = 0;
      }

      // How fast the helix is turning, smoothed: cards lift and shadows
      // spread while moving, then settle during the hold.
      const speed = Math.abs(this.rotation - this.prevRotation) / Math.max(1, dt); // deg per ms
      this.prevRotation = this.rotation;
      const motionTarget = clamp(speed / 0.09, 0, 1);
      this.motion += (motionTarget - this.motion) * Math.min(1, dt * (motionTarget > this.motion ? 0.012 : 0.004));

      this.render();
      this.frame = requestAnimationFrame((t) => this.tick(t));
    }

    /* ---------- Interaction ---------- */

    /** Rotate so card `index` sits front and centre (nearest equivalent turn). */
    bringToFront(index) {
      const span = this.total * this.stepAngle;
      let target = index * this.stepAngle;
      target += Math.round((this.rotation - target) / span) * span;
      this.velocity = 0;
      this.target = target;
    }

    step(direction) {
      this.stepClock = 0;
      const base = this.target ?? this.rotation;
      const snapped = Math.round(base / this.stepAngle) * this.stepAngle;
      this.velocity = 0;
      this.target = snapped + direction * this.stepAngle;
    }

    bind() {
      this.cleanup = [];
      const on = (target, type, fn, opts) => {
        target.addEventListener(type, fn, opts);
        this.cleanup.push(() => target.removeEventListener(type, fn, opts));
      };

      // Hover / focus pause
      on(this, 'pointerenter', (e) => {
        if (e.pointerType === 'mouse') this.hovered = true;
      });
      on(this, 'pointerleave', () => {
        this.hovered = false;
      });
      on(this, 'focusin', (e) => {
        this.focused = true;
        const card = e.target.closest('.spiral__card');
        if (card && !card.hasAttribute('data-clone')) {
          const entry = this.cards.find((c) => c.el === card);
          if (entry) this.bringToFront(entry.index);
        }
      });
      on(this, 'focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.focused = false;
      });

      // Prev / next buttons
      this.querySelectorAll('[data-spiral-prev], [data-spiral-next]').forEach((button) => {
        on(button, 'click', (e) => {
          e.preventDefault();
          this.classList.add('has-interacted');
          this.step(button.hasAttribute('data-spiral-next') ? 1 : -1);
        });
        // Buttons must not start a drag.
        on(button, 'pointerdown', (e) => e.stopPropagation());
      });

      // Keyboard: arrows step one card
      on(this, 'keydown', (e) => {
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (!(e.key in keys)) return;
        e.preventDefault();
        this.step(keys[e.key]);
      });

      // Drag / swipe with inertia
      let startX = 0;
      let startY = 0;
      let lastX = 0;
      let lastT = 0;
      let moved = false;
      let axisLocked = null;
      const degPerPx = () => 180 / Math.max(320, this.offsetWidth);

      on(this, 'pointerdown', (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        startX = lastX = e.clientX;
        startY = e.clientY;
        lastT = performance.now();
        moved = false;
        axisLocked = e.pointerType === 'mouse' ? 'x' : null;
        this.pointerId = e.pointerId;
        this.pointerType = e.pointerType;
        this.classList.add('has-interacted');
        this.target = null;
        this.velocity = 0;
        this.stepClock = 0;
        this.dragging = true;
      });
      on(window, 'pointermove', (e) => {
        if (!this.dragging || e.pointerId !== this.pointerId) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (!axisLocked && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
          axisLocked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (axisLocked === 'y') {
            this.dragging = false; // let the page scroll vertically
            return;
          }
        }
        if (axisLocked !== 'x') return;
        if (Math.abs(dx) > 6 && !moved) {
          moved = true;
          this.classList.add('is-dragging');
        }
        const now = performance.now();
        const step = (lastX - e.clientX) * degPerPx();
        this.rotation += step;
        this.velocity = step / Math.max(1, now - lastT);
        lastX = e.clientX;
        lastT = now;
      });
      const end = (e) => {
        if (!this.dragging || (e && e.pointerId !== this.pointerId)) return;
        this.dragging = false;
        this.classList.remove('is-dragging');
        // A click (if any) fires right after pointerup; disarm the guard after it.
        setTimeout(() => {
          moved = false;
        }, 60);
        if (performance.now() - lastT > 80) this.velocity = 0; // held still before release
        this.velocity = clamp(this.velocity, -1.2, 1.2);
        // Touch: land a card squarely in front (follows the flick direction).
        if (this.pointerType !== 'mouse' && moved) {
          const projected = this.rotation + this.velocity * 260;
          this.target = Math.round(projected / this.stepAngle) * this.stepAngle;
          this.velocity = 0;
          this.stepClock = 0;
        }
      };
      on(window, 'pointerup', end);
      on(window, 'pointercancel', end);
      // A drag must not turn into a click on the card under the pointer.
      on(
        this,
        'click',
        (e) => {
          if (e.target.closest('.spiral__controls')) return;
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          }
        },
        true
      );
      on(this, 'dragstart', (e) => e.preventDefault());

      // Optional: page scroll drives rotation
      if (this.settings.scrollDriven) {
        let lastScroll = window.scrollY;
        this.scrollDelta = 0;
        on(
          window,
          'scroll',
          () => {
            this.scrollDelta += window.scrollY - lastScroll;
            lastScroll = window.scrollY;
          },
          { passive: true }
        );
      }

      // Pause offscreen and in background tabs
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible) this.start();
          else this.stop();
        });
        io.observe(this);
        this.cleanup.push(() => io.disconnect());
      }
      on(document, 'visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.start();
      });

      // Re-measure on resize / breakpoint change
      let resizeTimer = null;
      on(window, 'resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.measure();
          this.buildLoop();
          this.cacheCards();
          this.render();
        }, 150);
      });

      // Theme editor: pause while the section is selected
      const sectionId = this.dataset.sectionId;
      on(document, 'shopify:section:select', (e) => {
        if (e.detail?.sectionId === sectionId) this.editorPaused = true;
      });
      on(document, 'shopify:section:deselect', (e) => {
        if (e.detail?.sectionId === sectionId) this.editorPaused = false;
      });
    }
  }

  customElements.define('spiral-collection-slider', SpiralCollectionSlider);
})();
